import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import SectionalDescriptionEditor from './SectionalDescriptionEditor';
import TagInput from './TagInput';
import { renderDescriptionHtml } from '../../utils/renderDescription';
import './Admin.css';

function slugify(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function generateArticleLink(categoryCode, title) {
  const cat = (categoryCode || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '') || 'uncategorized';
  const slug = slugify(title) || 'untitled';
  return `/kb/${cat}/${slug}`;
}

export default function AdminArticles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [categoryFilter, setCategoryFilter] = useState('__all__');
  const [sourceFilter, setSourceFilter] = useState('s');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [form, setForm] = useState({
    category_code: '', title: '', application: 'Back End Server Application',
    description: '', summary: '', tags: '', audience: '', category: '', articleLink: '', poster_image: '', video_url: '', status: 1, uid: 0, show_in_quick_links: 0,
  });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [categoryModalData, setCategoryModalData] = useState({ labelText: '', uri: '', descriptionText: '', imageSrc: '', application: [] });
  const [applicationModalData, setApplicationModalData] = useState({ name: '', source: 's', sort_order: 0, permissions: ['end_users'] });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [saveError, setSaveError] = useState('');
  const previewContentRef = useRef(null);
  const openedFromUrlRef = useRef(null);

  const syncEditorUrl = (nid, { category, source } = {}) => {
    const params = new URLSearchParams();
    if (nid) params.set('edit', String(nid));
    if (category && category !== '__all__') params.set('category', category);
    if (source) params.set('s', source);
    setSearchParams(params);
  };

  const clearEditorUrl = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('edit');
    setSearchParams(params, { replace: true });
  };

  // Mark portrait images in preview modal
  useEffect(() => {
    if (!showPreviewModal || !previewContentRef.current) return;
    const container = previewContentRef.current;
    const imgs = container.querySelectorAll('img');
    const checkPortrait = (img) => {
      if (img.naturalWidth && img.naturalHeight && img.naturalHeight > img.naturalWidth) {
        img.classList.add('img-portrait');
      }
    };
    imgs.forEach((img) => {
      if (img.complete) {
        checkPortrait(img);
      } else {
        img.addEventListener('load', () => checkPortrait(img));
      }
    });
  }, [showPreviewModal, form.description, form.poster_image]);

  const loadApplications = () => {
    api.get('/api/applications.php')
      .then((data) => {
        const apps = Array.isArray(data) ? data : [];
        setApplications(apps);
        setSourceFilter((prev) => {
          const hasMatch = apps.some((a) => (a.source ?? 's') === prev);
          return hasMatch ? prev : (apps[0]?.source ?? 's');
        });
      })
      .catch(() => setApplications([]));
  };

  const loadCategories = () => {
    api.get('/api/categories.php')
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  };

  const loadArticles = () => {
    if (!categoryFilter) { setList([]); setLoading(false); return; }
    setLoading(true);
    const categoryParam = categoryFilter === '__all__' ? '__all__' : categoryFilter;
    api.get(`/api/articles.php?category=${encodeURIComponent(categoryParam)}&s=${sourceFilter}`)
      .then((data) => { setList(Array.isArray(data) ? data : []); setMessage({ type: '', text: '' }); })
      .catch((err) => setMessage({ type: 'error', text: err.error || 'Failed to load' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadApplications(); loadCategories(); }, []);
  useEffect(() => { loadArticles(); }, [categoryFilter, sourceFilter]);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const sourceParam = searchParams.get('s');
    if (categoryParam) setCategoryFilter(categoryParam);
    if (sourceParam) setSourceFilter(sourceParam);
  }, [searchParams]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) {
      openedFromUrlRef.current = null;
      return;
    }
    const nid = parseInt(editId, 10);
    if (Number.isNaN(nid) || openedFromUrlRef.current === nid || editing === nid) return;
    openedFromUrlRef.current = nid;
    openEdit({ nid });
  }, [searchParams, editing]);

  // Auto-generate article link when category or title changes
  useEffect(() => {
    if (editing !== null || adding) {
      const link = generateArticleLink(form.category_code, form.title);
      setForm((prev) => (prev.articleLink !== link ? { ...prev, articleLink: link } : prev));
    }
  }, [form.category_code, form.title, editing, adding]);

  const openAdd = () => {
    setEditing(null);
    setAdding(true);
    const appBySource = applications.find((a) => (a.source ?? 's') === sourceFilter);
    setForm({
      category_code: (categoryFilter && categoryFilter !== '__all__') ? categoryFilter : '', title: '', application: appBySource?.name || applications[0]?.name || '',
      description: '', summary: '', tags: '', audience: '', category: '', articleLink: '', poster_image: '', video_url: '', status: 1, uid: 0, show_in_quick_links: 0,
    });
  };

  const openEdit = (row, { updateUrl = false } = {}) => {
    setAdding(false);
    setEditing(row.nid);
    setLoadingForm(true);
    if (updateUrl) {
      const app = applications.find((a) => a.name === row.application);
      syncEditorUrl(row.nid, {
        category: row.category_code || categoryFilter,
        source: app?.source ?? sourceFilter,
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    api.get(`/api/article.php?id=${row.nid}`)
      .then((data) => {
        const a = data?.article ?? data;
        setForm({
          category_code: (a?.category_code ?? row.category_code) || '', title: (a?.title ?? row.title) || '', application: (a?.application ?? row.application) || '',
          description: (a?.description ?? row.description) || '', summary: (a?.summary ?? row.summary) || '', tags: (a?.tags ?? row.tags) || '', audience: (a?.audience ?? row.audience) || '', category: (a?.category ?? row.category) || '',
          articleLink: (a?.articleLink ?? a?.article_link ?? row.articleLink ?? row.article_link) || '',
          poster_image: (a?.poster_image ?? row.poster_image) || '', video_url: (a?.video_url ?? row.video_url) || '',
          status: a?.status ?? row.status ?? 1, uid: a?.uid ?? row.uid ?? 0, show_in_quick_links: a?.show_in_quick_links ?? row.show_in_quick_links ?? 0,
        });
      })
      .catch(() => {
        setForm({
          category_code: row.category_code || '', title: row.title || '', application: row.application || '',
          description: row.description || '', summary: row.summary || '', tags: row.tags || '', audience: row.audience || '', category: row.category || '',
          articleLink: row.articleLink || row.article_link || '',
          poster_image: row.poster_image || '', video_url: row.video_url || '',
          status: row.status ?? 1, uid: row.uid ?? 0, show_in_quick_links: row.show_in_quick_links ?? 0,
        });
      })
      .finally(() => setLoadingForm(false));
  };

  const openPreview = (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setSaveError('');
    if (adding && !(form.category_code && form.category_code.trim())) {
      setMessage({ type: 'error', text: 'Please select a category.' });
      return;
    }
    if (!(form.title && form.title.trim())) {
      setMessage({ type: 'error', text: 'Title is required.' });
      return;
    }
    setShowPreviewModal(true);
  };

  const openPreviewInNewTab = () => {
    const bodyHtml = renderDescriptionHtml(form.description, 'https://admin.tablemate.net/sites/default/files');
    const videoHtml = (() => {
      if (!form.video_url) return '';
      const yt = form.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
      const vimeo = form.video_url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (yt) return `<iframe src="https://www.youtube.com/embed/${yt[1]}" allowfullscreen frameborder="0" style="max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px"></iframe>`;
      if (vimeo) return `<iframe src="https://player.vimeo.com/video/${vimeo[1]}" allowfullscreen frameborder="0" style="max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px"></iframe>`;
      return `<video src="${form.video_url}" controls${form.poster_image ? ` poster="${form.poster_image}"` : ''} style="max-width:100%"></video>`;
    })();
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preview: ${(form.title || 'Untitled').replace(/</g, '&lt;')}</title><style>
      body{font-family:system-ui,sans-serif;max-width:720px;margin:0 auto;padding:1.5rem;line-height:1.6;color:#333}
      h1{font-size:1.5rem;margin:0 0 1rem}
      .meta{color:#666;font-size:0.9rem;margin-bottom:1rem}
      .summary{background:#f8f9fa;padding:0.75rem;border-radius:6px;margin-bottom:1rem}
      .poster img{max-width:100%;max-height:300px;object-fit:contain;border-radius:6px}
      .video iframe,.video video{max-width:100%;width:100%;aspect-ratio:16/9;min-height:200px;border-radius:6px}
      .body img{max-width:100%;height:auto}
      .footer{margin-top:1rem;padding-top:1rem;border-top:1px solid #eee;font-size:0.9rem;color:#666}
    </style></head><body>
      <h1>${(form.title || '(Untitled)').replace(/</g, '&lt;')}</h1>
      <div class="meta"><strong>Category:</strong> ${(form.category_code || '—').replace(/</g, '&lt;')} &nbsp; <strong>Application:</strong> ${(form.application || '—').replace(/</g, '&lt;')}${form.show_in_quick_links ? ' &nbsp; <span style="background:#e8f4fd;color:#0a6ebd;padding:2px 8px;border-radius:4px">Quick links</span>' : ''}</div>
      ${form.summary ? `<div class="summary"><strong>Summary:</strong> ${form.summary.replace(/</g, '&lt;')}</div>` : ''}
      ${form.poster_image ? `<div class="poster"><img src="${form.poster_image}" alt="Poster" /></div>` : ''}
      ${form.video_url ? `<div class="video">${videoHtml}</div>` : ''}
      ${bodyHtml ? `<div class="body">${bodyHtml}</div>` : ''}
      ${(form.tags || form.audience || form.articleLink) ? `<div class="footer">${form.tags ? `<div><strong>Tags:</strong> ${(typeof form.tags === 'string' ? form.tags : (Array.isArray(form.tags) ? form.tags.join(', ') : '')).replace(/</g, '&lt;')}</div>` : ''}${form.audience ? `<div><strong>Audience:</strong> ${form.audience.replace(/</g, '&lt;')}</div>` : ''}${form.articleLink ? `<div><strong>Article link:</strong> <a href="${form.articleLink}">${form.articleLink.replace(/</g, '&lt;')}</a></div>` : ''}</div>` : ''}
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const save = async () => {
    setMessage({ type: '', text: '' });
    setSaveError('');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/article.php?id=${editing}`, form);
        setMessage({ type: 'success', text: 'Article updated.' });
      } else {
        const created = await api.post('/api/articles.php', form);
        setMessage({ type: 'success', text: created?.nid ? `Article #${created.nid} created.` : 'Article created.' });
      }
      setShowPreviewModal(false);
      setPreviewExpanded(false);
      setEditing(null);
      setAdding(false);
      openedFromUrlRef.current = null;
      clearEditorUrl();
      loadArticles();
    } catch (err) {
      const msg = err?.error || err?.message || (err?.status === 401 ? 'Not logged in. Please log in from the Admin dashboard.' : 'Save failed');
      setMessage({ type: 'error', text: msg });
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (nid) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/api/article.php?id=${nid}`);
      setMessage({ type: 'success', text: 'Article deleted.' });
      loadArticles();
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Delete failed' });
    }
  };

  const closeForm = () => {
    setEditing(null);
    setAdding(false);
    setLoadingForm(false);
    openedFromUrlRef.current = null;
    clearEditorUrl();
    setForm({ category_code: '', title: '', application: applications[0]?.name || '', description: '', summary: '', tags: '', audience: '', category: '', articleLink: '', poster_image: '', video_url: '', status: 1, uid: 0, show_in_quick_links: 0 });
  };

  const openCategoryModal = (appIdsOverride) => {
    const app = applications.find((a) => a.name === form.application);
    const defaultAppIds = app ? [app.id] : applications.map((a) => a.id).slice(0, 1);
    setCategoryModalData({
      labelText: '',
      uri: '',
      descriptionText: '',
      imageSrc: '',
      application: Array.isArray(appIdsOverride) && appIdsOverride.length ? appIdsOverride : defaultAppIds,
    });
    setShowCategoryModal(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    if (!categoryModalData.labelText?.trim()) {
      setMessage({ type: 'error', text: 'Label is required.' });
      return;
    }
    const uri = categoryModalData.uri?.trim() || categoryModalData.labelText.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (!uri) {
      setMessage({ type: 'error', text: 'URI is required.' });
      return;
    }
    try {
      const res = await api.post('/api/categories.php', {
        labelText: categoryModalData.labelText.trim(),
        uri,
        descriptionText: categoryModalData.descriptionText || '',
        imageSrc: categoryModalData.imageSrc || '',
        application: categoryModalData.application,
      });
      setMessage({ type: 'success', text: `Category "${res.labelText}" created.` });
      loadCategories();
      setForm((prev) => ({ ...prev, category_code: res.uri || uri }));
      setCategoryFilter(res.uri || uri);
      setShowCategoryModal(false);
    } catch (err) {
      setMessage({ type: 'error', text: err?.error || 'Failed to create category' });
    }
  };

  const openApplicationModal = () => {
    setApplicationModalData({
      name: '',
      source: sourceFilter || 's',
      sort_order: applications.length,
      permissions: ['end_users'],
    });
    setShowApplicationModal(true);
  };

  const togglePermission = (perm) => {
    setApplicationModalData((prev) => {
      const arr = Array.isArray(prev.permissions) ? [...prev.permissions] : [];
      const idx = arr.indexOf(perm);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(perm);
      return { ...prev, permissions: arr.length ? arr : ['end_users'] };
    });
  };

  const saveApplication = async (e) => {
    e.preventDefault();
    if (!applicationModalData.name?.trim()) {
      setMessage({ type: 'error', text: 'Name is required.' });
      return;
    }
    try {
      const res = await api.post('/api/applications.php', {
        name: applicationModalData.name.trim(),
        source: (applicationModalData.source || 's').trim(),
        sort_order: applicationModalData.sort_order,
        permissions: applicationModalData.permissions,
      });
      setMessage({ type: 'success', text: `Application "${res.name}" created.` });
      loadApplications();
      setForm((prev) => ({ ...prev, application: res.name }));
      setShowApplicationModal(false);
    } catch (err) {
      setMessage({ type: 'error', text: err?.error || 'Failed to create application' });
    }
  };

  return (
    <div className="admin-section">
      <h1>Articles</h1>
      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}
      <div className="admin-toolbar">
        <div className="admin-form row" style={{ marginBottom: 0, maxWidth: 'none', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div>
            <label>Category (uri)</label>
            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ width: '200px', marginBottom: 0 }}>
                <option value="">Select category</option>
                <option value="__all__">All</option>
                {categories.map((c) => (
                  <option key={c.uri} value={c.uri}>{c.labelText || c.uri}</option>
                ))}
              </select>
              <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => openCategoryModal(applications.filter((a) => (a.source ?? 's') === sourceFilter).map((a) => a.id))}>Add new</button>
            </div>
          </div>
          <div>
            <label>Source</label>
            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ width: '180px', marginBottom: 0 }}>
                {applications.map((app) => (
                  <option key={app.id} value={app.source ?? 's'}>{app.name}</option>
                ))}
              </select>
              <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={openApplicationModal}>Add new</button>
            </div>
          </div>
        </div>
        <button type="button" className="admin-btn admin-btn-primary" onClick={openAdd}>Add article</button>
      </div>
      {(editing !== null || adding) && (
        <div className="admin-form admin-form-wide">
          <h2>{editing ? 'Edit article' : 'New article'}</h2>
          {loadingForm ? (
            <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading article…</p>
          ) : (
          <form onSubmit={openPreview}>
            <label>Title *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Article title" />
            <div className="row">
              <div>
                <label>Category *</label>
                <div className="admin-form-row-with-action" style={{ marginBottom: 0 }}>
                  <select value={form.category_code} onChange={(e) => setForm({ ...form, category_code: e.target.value })} required style={{ flex: 1 }}>
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.uri} value={c.uri}>{c.labelText || c.uri}</option>
                    ))}
                  </select>
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={openCategoryModal}>Add new</button>
                </div>
              </div>
              <div>
                <label>Application</label>
                <div className="admin-form-row-with-action" style={{ marginBottom: 0 }}>
                  <select value={form.application} onChange={(e) => setForm({ ...form, application: e.target.value })} style={{ flex: 1 }}>
                    {applications.map((app) => (
                      <option key={app.id} value={app.name}>{app.name}</option>
                    ))}
                  </select>
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={openApplicationModal}>Add new</button>
                </div>
              </div>
            </div>
            <label>Summary <span style={{ fontWeight: 400, color: '#666' }}>(displayed below search results)</span></label>
            <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <label>Step-by-step instructions</label>
            <SectionalDescriptionEditor
              value={form.description}
              onChange={(v) => setForm({ ...form, description: v })}
              placeholder="Describe what to do in this step. Add images, links, or formatting as needed."
            />
            <label>Poster image</label>
            <div className="row" style={{ alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input value={form.poster_image} onChange={(e) => setForm({ ...form, poster_image: e.target.value })} placeholder="Image URL or upload below" />
              </div>
              <div>
                <input type="file" accept="image/*" id="poster-upload" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append('file', f);
                  fd.append('type', 'image');
                  try {
                    const r = await api.upload('/api/upload.php', fd);
                    if (r?.url) setForm((prev) => ({ ...prev, poster_image: r.url }));
                  } catch (err) {
                    setMessage({ type: 'error', text: err?.error || 'Upload failed' });
                  }
                  e.target.value = '';
                }} />
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => document.getElementById('poster-upload')?.click()}>Upload image</button>
              </div>
            </div>
            {form.poster_image && <div style={{ marginTop: '0.5rem' }}><img src={form.poster_image} alt="Poster" style={{ maxWidth: '200px', maxHeight: '120px', objectFit: 'contain', border: '1px solid #ccc' }} /></div>}
            <label>Video</label>
            <div className="row" style={{ alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Video URL (YouTube, Vimeo, or direct MP4) or upload below" />
              </div>
              <div>
                <input type="file" accept="video/*" id="video-upload" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append('file', f);
                  fd.append('type', 'video');
                  try {
                    const r = await api.upload('/api/upload.php', fd);
                    if (r?.url) setForm((prev) => ({ ...prev, video_url: r.url }));
                  } catch (err) {
                    setMessage({ type: 'error', text: err?.error || 'Upload failed' });
                  }
                  e.target.value = '';
                }} />
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => document.getElementById('video-upload')?.click()}>Upload video</button>
              </div>
            </div>
            <div className="row">
              <div>
                <label>Tags</label>
                <TagInput value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="Type to add or search tags…" />
              </div>
              <div><label>Audience</label><input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} /></div>
            </div>
            <label>Article link <span style={{ fontWeight: 400, color: '#666' }}>(auto: /kb/category/title)</span></label>
            <input value={form.articleLink} readOnly placeholder="/kb/category/title" style={{ background: '#eee', cursor: 'default' }} />
            <div className="admin-form-row-with-action" style={{ alignItems: 'center', marginBottom: '1rem' }}>
              <input type="checkbox" id="show_in_quick_links" checked={!!form.show_in_quick_links} onChange={(e) => setForm({ ...form, show_in_quick_links: e.target.checked ? 1 : 0 })} />
              <label htmlFor="show_in_quick_links" style={{ marginBottom: 0, cursor: 'pointer' }}>Show in Quick links</label>
            </div>
            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>Preview & Save</button>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
            </div>
          </form>
          )}
        </div>
      )}
      <div className="admin-table-wrap">
        {loading ? <p style={{ padding: '1rem' }}>Loading…</p> : !categoryFilter ? <p style={{ padding: '1rem' }}>Select a category to list articles.</p> : (
          <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Application</th>
                <th>Created by</th>
                <th>Last modified by</th>
                <th>Quick</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.nid}>
                  <td>{row.title}</td>
                  <td>{row.category_code}</td>
                  <td>{row.application || '—'}</td>
                  <td>{row.created_by || '—'}</td>
                  <td>{row.last_modified_by || '—'}</td>
                  <td>{row.show_in_quick_links ? '✓' : '—'}</td>
                  <td>{row.changed ? new Date(row.changed * 1000).toLocaleDateString() : '—'}</td>
                  <td>
                    <Link to={`/admin/articles/${row.nid}/versions`} className="admin-btn admin-btn-secondary admin-btn-small">History</Link>
                    <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => openEdit(row, { updateUrl: true })}>Edit</button>
                    <button type="button" className="admin-btn admin-btn-danger admin-btn-small" onClick={() => remove(row.nid)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {showCategoryModal && (
        <div className="admin-modal-backdrop" onClick={() => setShowCategoryModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add new category</h2>
              <button type="button" className="admin-modal-close" onClick={() => setShowCategoryModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={saveCategory}>
              <label>Label *</label>
              <input value={categoryModalData.labelText} onChange={(e) => setCategoryModalData({ ...categoryModalData, labelText: e.target.value })} required placeholder="Category label" />
              <label>URI</label>
              <input value={categoryModalData.uri} onChange={(e) => setCategoryModalData({ ...categoryModalData, uri: e.target.value })} placeholder="Auto-generated if empty (slug from label)" />
              <label>Description</label>
              <textarea value={categoryModalData.descriptionText} onChange={(e) => setCategoryModalData({ ...categoryModalData, descriptionText: e.target.value })} />
              <label>Image URL</label>
              <input value={categoryModalData.imageSrc} onChange={(e) => setCategoryModalData({ ...categoryModalData, imageSrc: e.target.value })} placeholder="Optional" />
              <label>Application</label>
              <select value={categoryModalData.application?.[0] ?? ''} onChange={(e) => {
                const v = e.target.value;
                setCategoryModalData({ ...categoryModalData, application: v ? [parseInt(v, 10)] : [] });
              }}>
                <option value="">Select application</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>{app.name}</option>
                ))}
              </select>
              <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
                <button type="submit" className="admin-btn admin-btn-primary">Create category</button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowCategoryModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="admin-modal-backdrop" onClick={() => { setShowPreviewModal(false); setPreviewExpanded(false); }}>
          <div className={`admin-modal admin-modal-preview ${previewExpanded ? 'admin-modal-preview-expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Preview &amp; confirm</h2>
              <div className="admin-modal-header-actions">
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={openPreviewInNewTab} title="Open in new tab">↗ New tab</button>
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => setPreviewExpanded((p) => !p)} title={previewExpanded ? 'Collapse' : 'Expand'}>
                  {previewExpanded ? '⊟ Collapse' : '⊞ Expand'}
                </button>
                <button type="button" className="admin-modal-close" onClick={() => { setShowPreviewModal(false); setPreviewExpanded(false); }} aria-label="Close">×</button>
              </div>
            </div>
            <div className="admin-preview-content" ref={previewContentRef}>
              <h3 className="admin-preview-title">{form.title || '(Untitled)'}</h3>
              <div className="admin-preview-meta">
                <span><strong>Category:</strong> {form.category_code || '—'}</span>
                <span><strong>Application:</strong> {form.application || '—'}</span>
                {form.show_in_quick_links ? <span className="admin-preview-badge">Quick links</span> : null}
              </div>
              {form.summary && (
                <div className="admin-preview-summary">
                  <strong>Summary:</strong> {form.summary}
                </div>
              )}
              {form.poster_image && (
                <div className="admin-preview-poster">
                  <img src={form.poster_image} alt="Poster" />
                </div>
              )}
              {form.video_url && (
                <div className="admin-preview-video">
                  {(() => {
                    const yt = form.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
                    const vimeo = form.video_url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                    if (yt) return <iframe src={`https://www.youtube.com/embed/${yt[1]}`} title="Video" allowFullScreen frameBorder="0" />;
                    if (vimeo) return <iframe src={`https://player.vimeo.com/video/${vimeo[1]}`} title="Video" allowFullScreen frameBorder="0" />;
                    return <video src={form.video_url} controls poster={form.poster_image || undefined} />;
                  })()}
                </div>
              )}
              {form.description && (
                <div
                  className="admin-preview-body"
                  dangerouslySetInnerHTML={{ __html: renderDescriptionHtml(form.description, 'https://admin.tablemate.net/sites/default/files') }}
                />
              )}
              {(form.tags || form.audience || form.articleLink) && (
                <div className="admin-preview-footer">
                  {form.tags && <div><strong>Tags:</strong> {typeof form.tags === 'string' ? form.tags : (Array.isArray(form.tags) ? form.tags.join(', ') : '')}</div>}
                  {form.audience && <div><strong>Audience:</strong> {form.audience}</div>}
                  {form.articleLink && <div><strong>Article link:</strong> <a href={form.articleLink} target="_blank" rel="noopener noreferrer">{form.articleLink}</a></div>}
                </div>
              )}
            </div>
            {saveError && <div className="admin-message error" style={{ margin: '0 1.5rem 1rem' }}>{saveError}</div>}
            <div className="admin-preview-actions">
              <button type="button" className="admin-btn admin-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Confirm & Save'}</button>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setShowPreviewModal(false); setSaveError(''); }} disabled={saving}>Back to edit</button>
            </div>
          </div>
        </div>
      )}

      {showApplicationModal && (
        <div className="admin-modal-backdrop" onClick={() => setShowApplicationModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add new application</h2>
              <button type="button" className="admin-modal-close" onClick={() => setShowApplicationModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={saveApplication}>
              <label>Name *</label>
              <input value={applicationModalData.name} onChange={(e) => setApplicationModalData({ ...applicationModalData, name: e.target.value })} required placeholder="Application name" />
              <label>Source</label>
              <input value={applicationModalData.source} onChange={(e) => setApplicationModalData({ ...applicationModalData, source: e.target.value })} placeholder="e.g. s, f, b (URL param ?s=)" />
              <label>Permissions</label>
              <div className="admin-checkbox-group">
                {['end_users', 'customer_staff', 'tablemate_staff'].map((perm) => (
                  <label key={perm} className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={(applicationModalData.permissions || []).includes(perm)}
                      onChange={() => togglePermission(perm)}
                    />
                    {perm.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
              <label>Sort order</label>
              <input type="number" value={applicationModalData.sort_order} onChange={(e) => setApplicationModalData({ ...applicationModalData, sort_order: parseInt(e.target.value, 10) || 0 })} />
              <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
                <button type="submit" className="admin-btn admin-btn-primary">Create application</button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowApplicationModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
